import qz from "qz-tray";
import { expandCombos } from "./combos";
import api from "./api";
import { bgInterval, keepTabAwake } from "./bgTimer";
import { buildTicketHtml, buildCancelTicketHtml, printTicket as printTicketBrowser, openAndPrint } from "./ticket";

let configured = false;
let warnedUnavailable = false;

export function configureQz() {
  if (configured) return;
  configured = true;
  qz.security.setCertificatePromise((resolve, reject) => {
    api.get("/qz/certificate")
      .then((r) => resolve(r.data.certificate))
      .catch(reject);
  });
  qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    api.post("/qz/sign", { toSign })
      .then((r) => resolve(r.data.signature))
      .catch(reject);
  });
}

export function isConnected() {
  try { return qz.websocket.isActive(); } catch { return false; }
}

let connecting = null;
let keepAliveTimer = null;
let wantConnected = false;
let failStreak = 0;
let nextTryAt = 0;
let qzState = "unknown";
let lastHealth = 0;

const emit = (s) => {
  if (s === qzState) return;
  qzState = s;
  window.dispatchEvent(new CustomEvent("qz-status", { detail: s }));
};
export const getQzState = () => qzState;

const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("QZ Tray no responde")), ms))]);

export async function connectQz(timeoutMs = 3000) {
  configureQz();
  if (qz.websocket.isActive()) { emit("connected"); return; }
  if (!connecting) {
    if (qzState === "unknown") emit("connecting");
    connecting = qz.websocket.connect({ retries: 1, delay: 1 })
      .then(() => { wantConnected = true; failStreak = 0; emit("connected"); keepTabAwake("pos-qz"); })
      .catch((e) => { failStreak += 1; nextTryAt = Date.now() + Math.min(60000, 10000 * 2 ** Math.min(failStreak - 1, 3)); emit("offline"); throw e; })
      .finally(() => { connecting = null; });
  }
  await withTimeout(connecting, timeoutMs);
}

export async function reconnectQz() {
  failStreak = 0; nextTryAt = 0;
  emit("connecting");
  if (qz.websocket.isActive()) { try { await qz.websocket.disconnect(); } catch (e) { /* ignore */ } }
  return connectQz(20000);
}

// Comprueba que QZ (Java) siga respondiendo; si se colgó, cierra y reconecta.
async function healthCheck() {
  lastHealth = Date.now();
  try {
    await withTimeout(qz.printers.getDefault(), 10000);
    emit("connected");
  } catch (e) {
    emit("offline");
    try { await qz.websocket.disconnect(); } catch (err) { /* ignore */ }
    connectQz(15000).catch(() => {});
  }
}

// Mantiene viva la conexión con QZ todo el tiempo, aun con la pestaña en segundo plano.
export function startQzKeepAlive() {
  configureQz();
  qz.websocket.setClosedCallbacks(() => {
    emit("offline");
    if (wantConnected) setTimeout(() => connectQz(15000).catch(() => {}), 2000);
  });
  if (keepAliveTimer) return;
  connectQz(15000).catch(() => {});
  const check = () => {
    if (connecting) return;
    if (!qz.websocket.isActive()) {
      if (Date.now() >= nextTryAt) connectQz(15000).catch(() => {});
      return;
    }
    if (Date.now() - lastHealth >= 60000) healthCheck();
  };
  keepAliveTimer = bgInterval(check, 10000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") { nextTryAt = 0; lastHealth = 0; check(); }
  });
}

export async function listPrinters() {
  await connectQz(30000);
  return qz.printers.find();
}

export async function qzTestConnection() {
  await connectQz(30000);
  const printers = await qz.printers.find();
  return printers || [];
}

export async function printTestPage(printerName) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    @page{size:80mm auto;margin:0}
    body{width:76mm;margin:0;padding:4mm;font-family:'Courier New',monospace;text-align:center}
    h2{font-size:18px;font-weight:900;margin:0 0 6px}
    .box{border:2px solid #000;padding:6px;margin-top:6px;font-weight:800}
  </style></head><body>
    <h2>PRUEBA DE IMPRESIÓN</h2>
    <div>QZ Tray conectado correctamente</div>
    <div class="box">${new Date().toLocaleString("es-MX")}</div>
    <div style="margin-top:8px;font-size:12px">ProfesionalPosRestaurante</div>
  </body></html>`;
  return printHtml(printerName, html);
}

const isMissingPrinter = (e) => /PrintService|is null|not found|cannot find|no se encontr|no longer available/i.test(e?.message || String(e || ""));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function resolvePrinter(name) {
  const list = (await qz.printers.find()) || [];
  const want = String(name || "").trim().toLowerCase();
  return list.find((p) => p === name) || list.find((p) => p.trim().toLowerCase() === want)
    || list.find((p) => p.toLowerCase().startsWith(want)) || null;
}

// Java puede fallar mientras Windows refresca su lista de impresoras: refresca, espera y reintenta (3 intentos).
async function printWithRetry(printerName, run) {
  await connectQz();
  let name = printerName;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await withTimeout(run(name), 45000);
    } catch (e) {
      if (!isMissingPrinter(e)) {
        if (qz.websocket.isActive() || attempt > 1) throw e;
        await connectQz(10000);
        continue;
      }
      if (attempt >= 3) throw new Error(`La impresora "${printerName}" no respondió después de 3 intentos (${e?.message || e}).`);
      await sleep(attempt * 1500);
      const found = await resolvePrinter(printerName).catch(() => null);
      if (!found && attempt === 2) throw new Error(`La impresora "${printerName}" no aparece en este equipo (apagada, desconectada o con otro nombre en Windows). Revísala y vuelve a elegirla en Configuración → Cocina e impresoras.`);
      if (found) name = found;
    }
  }
}

export function printHtml(printerName, html, widthMm = 80) {
  return printWithRetry(printerName, (name) => qz.print(qz.configs.create(name, {
    units: "mm",
    size: { width: Number(widthMm) || 80, height: null },
    margins: 0,
    scaleContent: true,
  }), [{ type: "pixel", format: "html", flavor: "plain", data: html }]));
}

// Modo texto (ESC/POS): se envía directo a la impresora, sin pasar por el render gráfico de Java.
export function printRaw(printerName, data) {
  return printWithRetry(printerName, (name) => qz.print(qz.configs.create(name), [{ type: "raw", format: "command", flavor: "plain", data }]));
}

// Dimensiones y tipografías según ancho de papel (58mm o 80mm)
function paperDims(size) {
  const is58 = String(size) === "58";
  return {
    is58,
    page: is58 ? "58mm" : "80mm",
    body: is58 ? "54mm" : "76mm",
    pad: is58 ? "2mm" : "3mm",
    area: is58 ? "17px" : "21px",
    folio: is58 ? "12px" : "14px",
    dest: is58 ? "15px" : "18px",
    cust: is58 ? "12px" : "14px",
    item: is58 ? "15px" : "18px",
    qtyBox: is58 ? "22px" : "28px",
    mods: is58 ? "11px" : "13px",
    note: is58 ? "12px" : "14px",
    foot: is58 ? "10px" : "12px",
    li: is58 ? "12px" : "14px",
    total: is58 ? "17px" : "22px",
    indent: is58 ? "26px" : "34px",
  };
}

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const ORDER_TYPE_LABEL = {
  mesa: "Mesa",
  rapida: "Venta rápida",
  llamada: "Llamada",
  whatsapp: "WhatsApp",
  llevar: "A domicilio",
  recoger: "Recoger",
  online: "Tienda en línea",
  kiosko: "Kiosko",
  ubereats: "Uber Eats",
  didi: "DiDi Food",
  rappi: "Rappi",
};

const FULFILLMENT_LABEL = { llevar: "DOMICILIO", recoger: "RECOGER" };

// Header/destination line for the comanda: channel + fulfillment (Domicilio/Recoger)
function comandaDest(order) {
  if (order.order_type === "mesa" && order.table) return `MESA: ${esc(order.table)}`;
  const channel = (ORDER_TYPE_LABEL[order.order_type] || order.order_type || "").toUpperCase();
  const fulfill = FULFILLMENT_LABEL[order.fulfillment];
  return fulfill ? `${esc(channel)} · ${esc(fulfill)}` : esc(channel);
}

// Customer block (name + phone) shown for non-table orders
function customerBlock(order) {
  if (order.order_type === "mesa") return "";
  const name = order.customer_name && order.customer_name !== "Cliente Kiosko" ? order.customer_name : "";
  const phone = order.phone || "";
  if (!name && !phone) return "";
  return `<div class="cust">
    ${name ? `<div><b>Cliente:</b> ${esc(name)}</div>` : ""}
    ${phone ? `<div><b>Tel:</b> ${esc(phone)}</div>` : ""}
  </div>`;
}

export function comandaHtml(area, order, items, size = "80") {
  const d = paperDims(size);
  const now = new Date();
  const hora = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const num = (order.folio || order.id || "").toString().slice(0, 8).toUpperCase();
  const rows = items.map((i) => {
    if (i.separator) return `<div class="sepc">— — SEPARAR PLATOS — —</div>`;
    const mods = [...(i.modifiers || []), ...(i.options || [])].filter(Boolean);
    return `
      <div class="item">
        <div class="line"><span class="qty">${esc(i.quantity)}</span><span class="nm">${esc(i.name)}</span></div>
        ${mods.length ? `<div class="mods">${mods.map((m) => `› ${esc(m)}`).join("<br/>")}</div>` : ""}
        ${i.notes ? `<div class="note">✎ ${esc(i.notes)}</div>` : ""}
      </div>`;
  }).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    @page { size: ${d.page} auto; margin: 0; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { width: ${d.body}; margin: 0; padding: ${d.pad}; font-family: 'Arial Black', Arial, sans-serif; color: #000; }
    .area { text-align: center; font-size: ${d.area}px; font-weight: 900; background:#000; color:#fff; padding: 5px 2px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 1px; }
    .head { display: flex; justify-content: space-between; align-items:center; font-size: ${d.folio}px; font-weight: 800; }
    .head .num { font-size: ${d.dest}px; }
    .dest { font-size: ${d.dest}px; font-weight: 900; margin: 5px 0 2px; text-transform:uppercase; }
    .cust { font-size: ${d.cust}px; font-weight: 700; margin: 2px 0 4px; }
    hr { border: none; border-top: 2px dashed #000; margin: 6px 0; }
    .item { margin-bottom: 7px; }
    .sepc { text-align:center; font-weight:900; border-top:3px double #000; border-bottom:3px double #000; padding:3px 0; margin:6px 0 8px; }
    .line { display: flex; gap: 6px; font-size: ${d.item}px; font-weight: 900; align-items:flex-start; }
    .qty { min-width: ${d.qtyBox}; text-align:center; border:2px solid #000; border-radius:4px; padding:0 2px; }
    .nm { flex:1; }
    .mods { font-size: ${d.mods}px; font-weight:700; margin-left: ${d.indent}; }
    .note { font-size: ${d.note}px; font-weight: 900; margin-left: ${d.indent}; margin-top:2px; text-transform: uppercase; background:#000; color:#fff; padding:1px 4px; display:inline-block; }
    .foot { text-align: center; font-size: ${d.foot}px; margin-top: 6px; font-weight:700; }
  </style></head><body>
    <div class="area">${esc(area.name)}</div>
    ${order.copy ? `<div class="area" style="background:#fff;color:#000;border:3px solid #000">COPIA</div><div class="foot">Reimpresa por ${esc(order.copy_by || "")} · ${esc(order.copy_at ? new Date(order.copy_at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "")}</div>` : ""}
    <div class="head"><span class="num">#${esc(num)}</span><span>${esc(hora)}</span></div>
    <div class="dest">${comandaDest(order)}</div>
    ${customerBlock(order)}
    <hr/>
    ${rows}
  </body></html>`;
}

const ESC = "\x1B";
const GS = "\x1D";
const plain = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[—–]/g, "-").replace(/[·›✎]/g, "-").replace(/[^\x20-\x7E]/g, "");
function wrap(text, width, indent = "", first = "") {
  const out = [];
  let line = "";
  const pre = () => (out.length ? indent : first);
  plain(text).split(/\s+/).filter(Boolean).forEach((w) => {
    if (line && line.length + 1 + w.length <= width) { line += " " + w; return; }
    if (line) { out.push(line); line = ""; }
    let p = pre();
    while (p.length + w.length > width) { out.push(p + w.slice(0, width - p.length)); w = w.slice(width - p.length); p = indent; }
    line = p + w;
  });
  if (line) out.push(line);
  return out.join("\n") + "\n";
}

// Comanda en texto ESC/POS (48 columnas en 80mm, 32 en 58mm).
export function comandaRaw(area, order, items, size = "80") {
  const cols = String(size) === "58" ? 32 : 48;
  const big = (on) => GS + "!" + (on ? "\x11" : "\x00");
  const tall = (on) => GS + "!" + (on ? "\x01" : "\x00");
  const bold = (on) => ESC + "E" + (on ? "\x01" : "\x00");
  const inv = (on) => GS + "B" + (on ? "\x01" : "\x00");
  const center = ESC + "a\x01";
  const left = ESC + "a\x00";
  const hr = "-".repeat(cols) + "\n";
  const hora = new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const num = (order.folio || order.id || "").toString().slice(0, 8).toUpperCase();
  const dest = comandaDest(order).replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  let out = ESC + "@" + center + inv(true) + big(true) + bold(true) + ` ${plain(area.name).toUpperCase()} ` + "\n" + big(false) + inv(false);
  if (order.copy) {
    out += big(true) + "COPIA\n" + big(false) + bold(false) + wrap(`Reimpresa por ${order.copy_by || ""} ${order.copy_at ? new Date(order.copy_at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}`, cols) + bold(true);
  }
  out += left + tall(true);
  const head = `#${num}`;
  out += head + " ".repeat(Math.max(1, cols - head.length - hora.length)) + hora + "\n";
  out += wrap(dest.toUpperCase(), cols) + tall(false);
  if (order.order_type !== "mesa") {
    const name = order.customer_name && order.customer_name !== "Cliente Kiosko" ? order.customer_name : "";
    if (name) out += wrap(`Cliente: ${name}`, cols);
    if (order.phone) out += wrap(`Tel: ${order.phone}`, cols);
  }
  out += bold(false) + hr;
  items.forEach((i) => {
    if (i.separator) { out += center + bold(true) + "== SEPARAR PLATOS ==\n" + bold(false) + left; return; }
    const q = `${i.quantity} `;
    const pad = " ".repeat(q.length);
    out += tall(true) + bold(true) + wrap(q + i.name, cols, pad) + bold(false) + tall(false);
    [...(i.modifiers || []), ...(i.options || [])].filter(Boolean).forEach((m) => { out += wrap(`> ${m}`, cols, pad + "  ", pad); });
    if (i.notes) out += bold(true) + inv(true) + wrap(` NOTA: ${String(i.notes).toUpperCase()} `, cols, pad, pad) + inv(false) + bold(false);
    out += "\n";
  });
  return out + "\n\n\n\n" + GS + "V\x42\x00";
}

// Cashier order note (for pickup orders) — shows total to collect
function orderNoteHtml(settings, order) {
  const s = settings || {};
  const d = paperDims(s.ticket_size);
  const now = new Date();
  const hora = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const num = (order.folio || order.id || "").toString().slice(0, 8).toUpperCase();
  const total = (order.subtotal || 0);
  const items = (order.items || []).filter((i) => !i.separator).map((i) =>
    `<div class="li"><span>${esc(i.quantity)}x ${esc(i.name)}</span><span>$${((i.price || 0) * (i.quantity || 0)).toFixed(2)}</span></div>`
  ).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    @page { size: ${d.page} auto; margin: 0; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { width: ${d.body}; margin: 0; padding: ${d.pad}; font-family: 'Arial Black', Arial, sans-serif; color: #000; }
    .title { text-align: center; font-size: ${d.dest}px; font-weight: 900; background:#000; color:#fff; padding: 5px 2px; text-transform: uppercase; letter-spacing:1px; }
    .dest { text-align:center; font-size: ${d.dest}px; font-weight: 900; margin: 6px 0 2px; text-transform:uppercase; }
    .cust { font-size: ${d.cust}px; font-weight: 700; }
    .head { display: flex; justify-content: space-between; font-size: ${d.folio}px; font-weight: 800; margin-top:4px; }
    hr { border: none; border-top: 2px dashed #000; margin: 6px 0; }
    .li { display:flex; justify-content:space-between; gap:8px; font-size:${d.li}px; font-weight:700; margin:3px 0; }
    .total { display:flex; justify-content:space-between; font-size:${d.total}px; font-weight:900; border-top:3px solid #000; padding-top:6px; margin-top:6px; }
    .foot { text-align:center; font-size:${d.foot}px; margin-top:8px; font-weight:800; text-transform:uppercase; }
  </style></head><body>
    <div class="title">Nota de orden · Caja</div>
    <div class="head"><span>#${esc(num)}</span><span>${esc(hora)}</span></div>
    <div class="dest">${comandaDest(order)}</div>
    ${customerBlock(order)}
    <hr/>
    ${items}
    <div class="total"><span>TOTAL A COBRAR</span><span>$${total.toFixed(2)}</span></div>
    <div class="foot">Pendiente de cobro · pago al recoger</div>
  </body></html>`;
}

/**
 * Prints the cashier "order note" (with total to collect) to the caja printer.
 * Falls back to a browser print window if QZ Tray is unavailable.
 */
export async function printOrderNote(settings, order) {
  const printer = ((settings && settings.printer_caja) || "").trim();
  const width = (settings && settings.ticket_size) || "80";
  const html = orderNoteHtml(settings, order);
  if (printer) {
    try {
      await connectQz();
      await printHtml(printer, html, width);
      return { via: "qz" };
    } catch (e) {
      openAndPrint(html);
      return { via: "browser", error: e?.message || String(e) };
    }
  }
  openAndPrint(html);
  return { via: "browser" };
}

/**
 * Groups the order items by comanda area and prints one comanda per area
 * that has products, sending each directly to its assigned QZ printer.
 * Returns { printed, skippedNoPrinter, unassigned }.
 * Throws if QZ Tray is not available (caller shows the warning).
 */
export function groupComandaAreas(order, areas) {
  const areaById = {};
  (areas || []).forEach((a) => { areaById[a.id] = a; });
  const groups = {};
  expandCombos(order.items).forEach((i) => {
    if (i.separator) {
      Object.values(groups).forEach((g) => { if (g.length && !g[g.length - 1].separator) g.push(i); });
      return;
    }
    if (i.requires_comanda === false) return;
    const aid = i.area_id;
    if (!aid || !areaById[aid]) return;
    (groups[aid] = groups[aid] || []).push(i);
  });
  Object.values(groups).forEach((g) => { while (g.length && g[g.length - 1].separator) g.pop(); });
  return { groups, areaById };
}

// Imprime una comanda de la cola por área; salta las áreas ya impresas y avisa cada área terminada.
export async function printJobAreas(order, areas, size, skip = [], onAreaDone = () => {}) {
  const { groups, areaById } = groupComandaAreas(order, areas);
  const ids = Object.keys(groups).filter((a) => !skip.includes(a));
  if (!ids.length) return { errors: [] };
  await connectQz();
  const errors = [];
  for (const aid of ids) {
    const area = areaById[aid];
    if (!area.printer_name) { errors.push(`${area.name} sin impresora asignada`); continue; }
    try {
      await printRaw(area.printer_name, comandaRaw(area, order, groups[aid], size));
      onAreaDone(aid);
    } catch (e) { errors.push(`${area.name}: ${e?.message || "error de impresora"}`); }
  }
  return { errors };
}

export async function printComandasByArea(order, areas, size = "80") {
  const areaById = {};
  (areas || []).forEach((a) => { areaById[a.id] = a; });

  const groups = {};
  expandCombos(order.items).forEach((i) => {
    if (i.separator) {
      Object.values(groups).forEach((g) => { if (g.length && !g[g.length - 1].separator) g.push(i); });
      return;
    }
    if (i.requires_comanda === false) return;
    const aid = i.area_id;
    if (!aid || !areaById[aid]) return; // producto sin área asignada
    (groups[aid] = groups[aid] || []).push(i);
  });
  Object.values(groups).forEach((g) => { while (g.length && g[g.length - 1].separator) g.pop(); });

  const areaIds = Object.keys(groups);
  if (areaIds.length === 0) return { printed: 0, skippedNoPrinter: 0, unassigned: true };

  await connectQz(); // throws if QZ Tray not running

  let printed = 0;
  let skippedNoPrinter = 0;
  const errors = [];
  for (const aid of areaIds) {
    const area = areaById[aid];
    if (!area.printer_name) { skippedNoPrinter += 1; continue; }
    try {
      await printRaw(area.printer_name, comandaRaw(area, order, groups[aid], size));
      printed += 1;
    } catch (e) { errors.push(`${area.name}: ${e?.message || "error de impresora"}`); }
  }
  return { printed, skippedNoPrinter, unassigned: false, errors };
}

export function resetQzWarning() { warnedUnavailable = false; }
export function shouldWarnOnce() {
  if (warnedUnavailable) return false;
  warnedUnavailable = true;
  return true;
}

/**
 * Prints the account/receipt ticket to the assigned cash (caja) printer via QZ Tray.
 * If no caja printer is configured or QZ Tray is unavailable, falls back to the
 * browser print window so the ticket is never lost.
 * Returns { via: 'qz' | 'browser', error? }.
 */
export async function printCancelTicketRouted(settings, order) {
  const printer = ((settings && settings.printer_caja) || "").trim();
  const html = buildCancelTicketHtml(settings, order);
  if (printer) {
    try { await connectQz(); await printHtml(printer, html, (settings && settings.ticket_size) || "80"); return { via: "qz" }; } catch (e) { /* fallback */ }
  }
  openAndPrint(html);
  return { via: "browser" };
}

export async function printTicketRouted(settings, order) {
  const printer = ((settings && settings.printer_caja) || "").trim();
  const width = (settings && settings.ticket_size) || "80";
  const copies = Math.max(1, Math.min(5, parseInt(settings && settings.ticket_copies, 10) || 1));
  if (printer) {
    try {
      await connectQz();
      for (let i = 0; i < copies; i += 1) {
        await printHtml(printer, buildTicketHtml(settings, order), width);
      }
      return { via: "qz" };
    } catch (e) {
      printTicketBrowser(settings, order, copies);
      return { via: "browser", error: e?.message || String(e) };
    }
  }
  printTicketBrowser(settings, order, copies);
  return { via: "browser" };
}

export default qz;
